"use client";

import { useEffect, useRef, useState } from "react";
import { Droplets, ShieldCheck, Sparkles, Diamond } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  icon: typeof Droplets;
  label: string;
  ceramicValue: number;
  ceramicSuffix: string;
  ceramicLabel: string;
  waxLabel: string;
  metric: string;
};
const stats: Stat[] = [{
  icon: ShieldCheck,
  label: "Closing Speed",
  ceramicValue: 7,
  ceramicSuffix: "d",
  ceramicLabel: "Days",
  waxLabel: "6 months",
  metric: "Timeline"
}, {
  icon: Sparkles,
  label: "Equity Realized",
  ceramicValue: 98,
  ceramicSuffix: "%",
  ceramicLabel: "Cash Value",
  waxLabel: "60%",
  metric: "Payout"
}, {
  icon: Diamond,
  label: "Repair Effort",
  ceramicValue: 0,
  ceramicSuffix: "%",
  ceramicLabel: "No Repairs",
  waxLabel: "2H",
  metric: "Requirement"
}, {
  icon: Droplets,
  label: "Transaction Ease",
  ceramicValue: 100,
  ceramicSuffix: "%",
  ceramicLabel: "Hassle-Free",
  waxLabel: "Low",
  metric: "Experience"
}];
function AnimatedNumber({
  target,
  suffix,
  start
}: {
  target: number;
  suffix: string;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1600;
    const startTime = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target]);
  return <span className="font-default tabular-nums bg-gradient-to-b from-foreground via-foreground/90 to-foreground/40 bg-clip-text text-transparent">
      {value}
      {suffix}
    </span>;
}
export default function TheBenefitsOfSellingToUs() {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: compareRef,
    isVisible: compareVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const updateSlider = (clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = (clientX - rect.left) / rect.width * 100;
    setSliderPos(Math.max(0, Math.min(100, pct)));
  };
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent | TouchEvent) => {
      const x = "touches" in e ? e.touches[0]?.clientX ?? 0 : (e as MouseEvent).clientX;
      updateSlider(x);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [isDragging]);
  const startDrag = () => {
    setIsDragging(true);
    setHasInteracted(true);
  };
  return <section data-section-id="2694" id="the-benefits-of-selling-to-us" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4 md:px-8">
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at 50% 30%, hsl(45 90% 45% / 0.1), transparent 60%)"
    }} />
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse at 50% 100%, hsl(45 90% 45% / 0.05), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-6">
            — The Advantage —
          </p>
          <h2 className="font-default font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Traditional sales lag.{" "}
            <span className="italic bg-gradient-to-b from-foreground via-foreground/80 to-foreground/30 bg-clip-text text-transparent">
              Luxury Construction delivers.
            </span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Drag the slider to compare the stress of a traditional market listing
            versus the speed and certainty of a direct cash offer from Luxury Construction LLC.
          </p>
        </div>

        <div ref={compareRef} className={`mt-16 md:mt-20 transition-all duration-1000 ease-out ${compareVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div ref={containerRef} className="relative w-full aspect-[16/9] overflow-hidden border border-border shadow-lg select-none" style={{
          cursor: isDragging ? "ew-resize" : "default"
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/traditional_market_listing.png" alt="Traditional home listing with realtor signs and long wait times" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />

            <div className="absolute inset-0 overflow-hidden" style={{
            clipPath: `inset(0 0 0 ${sliderPos}%)`
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/luxury_construction_cash_offer.png" alt="Luxury Construction LLC fast cash offer and quick closing" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            </div>

            <div className="absolute top-4 left-4 md:top-6 md:left-6 max-w-[45%]">
              <div className="inline-flex flex-col bg-background/90 backdrop-blur-sm border border-border px-3 py-2 md:px-4 md:py-3">
                <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-muted-foreground">
                  Traditional Sale
                </span>
                <span className="font-default text-xs md:text-sm text-foreground/90 mt-1">
                  Months of uncertainty
                </span>
              </div>
            </div>
            <div className="absolute top-4 right-4 md:top-6 md:right-6 max-w-[45%] text-right">
              <div className="inline-flex flex-col bg-background/90 backdrop-blur-sm border border-border px-3 py-2 md:px-4 md:py-3">
                <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-primary">
                  Luxury Cash Offer
                </span>
                <span className="font-default text-xs md:text-sm text-foreground/90 mt-1">
                  Fast, guaranteed closing
                </span>
              </div>
            </div>

            <div className="absolute top-0 bottom-0 w-px pointer-events-none" style={{
            left: `${sliderPos}%`,
            background: "linear-gradient(to bottom, hsl(45 90% 45% / 0.3), hsl(45 90% 45% / 0.9), hsl(45 90% 45% / 0.3))",
            boxShadow: "0 0 20px hsl(45 90% 45% / 0.4)"
          }} />

            <div className="absolute top-1/2 z-10 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 border-2 border-primary" style={{
            left: `${sliderPos}%`,
            transform: "translate(-50%, -50%)",
            background: "hsl(45 90% 45%)",
            cursor: "ew-resize"
          }} onMouseDown={startDrag} onTouchStart={startDrag} role="slider" aria-label="Drag to compare" tabIndex={0}>
              {!hasInteracted && <span className="absolute inset-0 animate-ping" style={{
              background: "hsl(45 90% 45% / 0.4)"
            }} />}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="relative text-foreground">
                <path d="M5 4 L2 8 L5 12 M11 4 L14 8 L11 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <div ref={statsRef} className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {stats.map((stat, i) => {
          const Icon = stat.icon;
          return <div key={stat.label} data-index={i} className="bg-background p-6 md:p-8 flex flex-col gap-4 transition-colors hover:bg-accent" style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                    {stat.metric}
                  </span>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-5xl md:text-6xl font-default font-light leading-none">
                    <AnimatedNumber target={stat.ceramicValue} suffix={stat.ceramicSuffix} start={statsVisible} />
                  </span>
                </div>

                <div>
                  <h3 className="font-default font-semibold text-lg md:text-xl text-foreground">
                    {stat.label}
                  </h3>
                  <div className="mt-3 space-y-1.5 font-mono text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground tracking-wider uppercase">
                        Luxury
                      </span>
                      <span className="text-foreground">
                        {stat.ceramicLabel}
                      </span>
                    </div>
                    <div className="flex items-center justify-between opacity-60">
                      <span className="text-muted-foreground tracking-wider uppercase">
                        Market
                      </span>
                      <span className="text-muted-foreground line-through">
                        {stat.waxLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-16 md:mt-20 flex flex-col items-center text-center">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">
            Ready for a fair offer?
          </p>
          <Button asChild size="lg" className="font-default tracking-wide px-8 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/SellYourHouseFastPage">Get Your Cash Offer</Link>
          </Button>
          <p className="mt-5 text-sm text-muted-foreground max-w-md">
            Nationwide coverage. No realtor commissions. We buy as-is, in any condition.
          </p>
        </div>
      </div>
    </section>;
}